import React, { useMemo, useRef, useState } from "react";
import "./BarcodeInformation.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:5000/api"
    : "/api");

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const getStatusClass = (status = "") => {
  const s = String(status).toUpperCase();

  if (s.includes("DELIVERED")) return "bi-status bi-status-delivered";
  if (s.includes("RELEASED")) return "bi-status bi-status-released";
  if (s.includes("RETURNED")) return "bi-status bi-status-returned";
  if (s.includes("CANCEL")) return "bi-status bi-status-cancelled";
  if (s.includes("NO DN")) return "bi-status bi-status-warning";
  if (s.includes("IN STOCK")) return "bi-status bi-status-in";

  return "bi-status";
};

const InfoItem = ({ label, value }) => (
  <div className="bi-info-item">
    <span>{label}</span>
    <strong>{formatValue(value)}</strong>
  </div>
);

export default function BarcodeInformation() {
  const inputRef = useRef(null);

  const [barcode, setBarcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const releasedTo = useMemo(() => {
    if (!result?.release) return null;

    return (
      result.release.releasedTo ||
      result.release.client || {
        customerID: null,
        customerName: null,
        shipTo: null,
      }
    );
  }, [result]);

  const handleSearch = async (e) => {
    e?.preventDefault();

    const cleanBarcode = barcode.trim();

    if (!cleanBarcode) {
      setError("Please scan or enter a barcode.");
      setResult(null);
      inputRef.current?.focus();
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/stock/barcode-info/${encodeURIComponent(cleanBarcode)}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Failed to load barcode information.");
      }

      setResult(data);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setBarcode("");
    setResult(null);
    setError("");
    inputRef.current?.focus();
  };

  return (
    <div className="bi-page">
      <div className="bi-header">
        <div>
          <p className="bi-eyebrow">Inventory Barcode Lookup</p>
          <h1>Barcode Information</h1>
          <p>
            Scan or enter a barcode to view catalog, lot, expiry, stock status,
            DN, SO, and release destination.
          </p>
        </div>
      </div>

      <form className="bi-search-card" onSubmit={handleSearch}>
        <div className="bi-search-input-wrap">
          <label>Scan / Enter Barcode</label>
          <input
            ref={inputRef}
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            placeholder="Example: OBX-LE8BAG"
            autoFocus
          />
        </div>

        <div className="bi-actions">
          <button type="submit" disabled={loading}>
            {loading ? "Checking..." : "Search"}
          </button>

          <button type="button" className="bi-clear-btn" onClick={handleClear}>
            Clear
          </button>
        </div>
      </form>

      {error && <div className="bi-alert">{error}</div>}

      {result && (
        <div className="bi-result-grid">
          <section className="bi-main-card">
            <div className="bi-card-top">
              <div>
                <p className="bi-eyebrow">Scanned Barcode</p>
                <h2>{result.barcode}</h2>
              </div>

              <span className={getStatusClass(result.status)}>
                {result.status}
              </span>
            </div>

            <div className="bi-summary">
              <InfoItem label="Catalog Number" value={result.catalogNumber} />
              <InfoItem label="Item Name" value={result.itemName} />
              <InfoItem label="Lot Number" value={result.lotNumber} />
              <InfoItem label="Expiry Date" value={result.expiryDate} />
              <InfoItem label="Barcode Type" value={result.barcodeType} />
              <InfoItem label="Box Barcode" value={result.boxBarcode} />
            </div>
          </section>

          <section className="bi-card">
            <div className="bi-section-title">
              <h3>Stock Details</h3>
              <span>{formatValue(result.stock?.warehouseName)}</span>
            </div>

            <div className="bi-summary">
              <InfoItem label="Stock ID" value={result.stock?.stockID} />
              <InfoItem label="Box Stock ID" value={result.stock?.boxStockID} />
              <InfoItem label="Stock Status" value={result.stock?.stockStatus} />
              <InfoItem label="Quantity" value={result.stock?.quantity} />
              <InfoItem label="Pieces / Box" value={result.stock?.piecesPerBox} />
              <InfoItem label="Received Date" value={result.stock?.receivedDate} />
              <InfoItem label="Stock Source" value={result.stock?.stockSource} />
            </div>
          </section>

          {result.release ? (
            <>
              <section className="bi-card bi-release-card">
                <div className="bi-section-title">
                  <h3>Released To</h3>
                  <span>Client / Branch</span>
                </div>

                <div className="bi-release-name">
                  {formatValue(
                    releasedTo?.customerName ||
                    result.release?.client?.customerName ||
                    "Customer name not available"
                  )}
                </div>

                <div className="bi-summary">
                  <InfoItem
                    label="Customer ID"
                    value={result.release?.client?.customerID}
                  />
                  <InfoItem
                    label="Customer Number"
                    value={result.release?.client?.customerNumber}
                  />
                  <InfoItem
                    label="Ship To / Location"
                    value={
                      result.release?.releasedTo?.location ||
                      result.release?.client?.shipTo
                    }
                  />
                  <InfoItem
                    label="Released Date"
                    value={result.release?.releasedDate}
                  />
                  <InfoItem
                    label="Delivered Date"
                    value={result.release?.deliveredDate}
                  />
                  <InfoItem
                    label="Delivered By"
                    value={result.release?.dn?.deliveredBy}
                  />
                </div>
              </section>

              <section className="bi-card">
                <div className="bi-section-title">
                  <h3>Delivery Note</h3>
                  <span>{formatValue(result.release?.dn?.dnProcessStatus)}</span>
                </div>

                <div className="bi-summary">
                  <InfoItem label="DN Number" value={result.release?.dn?.dnNumber} />
                  <InfoItem label="DN Date" value={result.release?.dn?.dnDate} />
                  <InfoItem label="DN Status" value={result.release?.dn?.dnStatus} />
                  <InfoItem
                    label="Process Status"
                    value={result.release?.dn?.dnProcessStatus}
                  />
                  <InfoItem label="Created By" value={result.release?.dn?.createdBy} />
                  <InfoItem
                    label="Released Barcode"
                    value={result.release?.releasedBarcode}
                  />
                  <InfoItem
                    label="Qty Delivered"
                    value={result.release?.qtyDelivered}
                  />
                  <InfoItem
                    label="Item Status"
                    value={result.release?.itemStatus}
                  />
                </div>
              </section>

              <section className="bi-card">
                <div className="bi-section-title">
                  <h3>Sales Order</h3>
                  <span>{formatValue(result.release?.salesOrder?.deliveryStatus)}</span>
                </div>

                <div className="bi-summary">
                  <InfoItem
                    label="SO Number"
                    value={result.release?.salesOrder?.soNumber}
                  />
                  <InfoItem
                    label="Reference Order"
                    value={result.release?.salesOrder?.referenceOrder}
                  />
                  <InfoItem
                    label="SO Status"
                    value={result.release?.salesOrder?.soStatus}
                  />
                  <InfoItem
                    label="Delivery Status"
                    value={result.release?.salesOrder?.deliveryStatus}
                  />
                </div>
              </section>
            </>
          ) : (
            <section className="bi-card bi-no-release">
              <h3>No DN Release Record</h3>
              <p>
                This barcode is OUT, but there is no matching Delivery Note item.
                It may have been manually stocked out, scanned out, expired, or
                adjusted without client release details.
              </p>
            </section>
          )}
        </div>
      )}
    </div>
  );
}